import { Box, IconButton, Tooltip } from '@mui/material'
import { Mic as MicIcon } from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  RECORDING_CTA_GLYPH_PX,
  RECORDING_CTA_OUTLINE,
  RECORDING_CTA_SIZE,
} from '@/apps/latest/shared/styles/recordingCta'

import type { ViewStatus } from '@/apps/latest/shared/recording-status/types'
import { RADIUS } from '@/shared/styles/radii'
import { useAudioPulse } from './useAudioPulse'

// 録音 CTA の主役 control. idle → 録音開始 (mic), recording → 停止 を同一位置で
// トグルする (= 別所に停止を置かない).
// idle / recording は同じボタン・地塗りなしに揃え、 色と icon だけで区別する.
// 寸法・輪郭は RECORDING_CTA_* 共有定数 (56px/mic 28px/輪郭 1.5px 40% = 共有 spec
// 48px・輪郭なしからの app 拡大. rationale は recordingCta.ts と
// recording-control.md アプリ差).
//   idle = accent の mic icon, recording = success 緑の小さな角丸スクエア.
//   停止スクエアは一回り小さく取る (= 塗り四角は mic グリフより視覚的質量が大きく、 同 px
//   だと「デカく」見えるため. 小さくして開始ボタンと同じ大きさに揃える).
//   - 録音中だけ周囲に脈動 ring (useAudioPulse, 音声振幅連動). 拡張は控えめ・感度高め.
// 重要操作ゆえ hover 隠しはせず常時可視. tooltip / aria-label は保持.
interface Props {
  status: ViewStatus
  // 録音中の音量で脈動させるための MediaStream (= engine.stream).
  stream: MediaStream | null
  // idle で押下 (= 録音開始).
  onStart: () => void
  // recording で押下 (= 録音停止).
  onStop: () => void
  // 開始前の必須情報が揃うまで押させない (まとめて録音: 患者行の取得中). 見た目は
  // stopping と同じ fg.disabled (design-book recording-control.md の状態). 録音中には効かない.
  disabled?: boolean
}

const SIZE = RECORDING_CTA_SIZE
// 停止スクエアの寸法 (SIZE とセットで拡大. ICON_SIZE 枠外の decorative 指定).
const STOP_SQUARE_PX = 18

export const RecordingControl = ({
  status,
  stream,
  onStart,
  onStop,
  disabled = false,
}: Props) => {
  const isRecording = status === 'recording'
  // stopping は停止処理中 (= 押下不可). idle / recording のみ active.
  const isStopping = status === 'stopping'
  const label = isRecording ? '録音停止' : '録音開始'

  // 録音中だけ ring を音声振幅に連動させる. ref を脈動 ring 要素に attach.
  const ringRef = useAudioPulse(stream, isRecording)

  return (
    <Tooltip
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span">{label}</Box>
          <Box component="span" sx={{ opacity: 0.7, fontSize: 11 }}>
            ⇧Enter
          </Box>
        </Box>
      }
      placement="bottom"
    >
      <Box
        sx={{
          position: 'relative',
          width: SIZE,
          height: SIZE,
          flexShrink: 0,
        }}
      >
        {/* 録音中だけ脈動 ring. transform/opacity は useAudioPulse が rAF で直接書く.
            押下を妨げないよう pointer-events なし. */}
        {isRecording && (
          <Box
            ref={ringRef}
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${colorTokens.state.successFg}`,
              opacity: 0.2,
              pointerEvents: 'none',
              willChange: 'transform, opacity',
            }}
          />
        )}
        <IconButton
          aria-label={label}
          onClick={isRecording ? onStop : onStart}
          disabled={isStopping || (!isRecording && disabled)}
          sx={{
            width: SIZE,
            height: SIZE,
            color: isRecording
              ? colorTokens.state.successFg
              : colorTokens.accent.primary,
            border: RECORDING_CTA_OUTLINE,
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: colorTokens.overlay.selected,
            },
            '&.Mui-disabled': {
              bgcolor: 'transparent',
              color: colorTokens.fg.disabled,
            },
          }}
        >
          {isRecording ? (
            <Box
              aria-hidden
              sx={{
                width: STOP_SQUARE_PX,
                height: STOP_SQUARE_PX,
                borderRadius: `${RADIUS.sm}px`,
                bgcolor: 'currentColor',
              }}
            />
          ) : (
            <MicIcon sx={{ fontSize: RECORDING_CTA_GLYPH_PX }} />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  )
}
